import type { DataTableQuery } from '../../../shared/components/DataTable';
import type { NotaFiscalEmpresaStatus } from './types';

/** Tamanho de página da primeira abertura, o mesmo padrão do GeneXus. */
export const NF_EMPRESA_PAGE_SIZE = 20;

const STORAGE_PREFIX = 'dtsadmin:nf-empresa-grid:';

export interface NfEmpresaGridSnapshot {
  empresaId: number | null;
  query: DataTableQuery;
  status: NotaFiscalEmpresaStatus | '';
  periodoAno: number | '';
  periodoMes: number | '';
  emissaoAno: number | '';
  emissaoMes: number | '';
  clienteCodigo: number | null;
  requisicaoCompraReferencia: string;
  colaboradorCodigo: number | null;
}

const STATUS: readonly NotaFiscalEmpresaStatus[] = ['PEM', 'EMI', 'CAN', 'PCA'];

export function defaultNfEmpresaGridSnapshot(): NfEmpresaGridSnapshot {
  return {
    empresaId: null,
    query: {
      page: 1,
      pageSize: NF_EMPRESA_PAGE_SIZE,
      sortBy: 'dataHora',
      sortOrder: 'desc',
    },
    status: '',
    periodoAno: '',
    periodoMes: '',
    emissaoAno: '',
    emissaoMes: '',
    clienteCodigo: null,
    requisicaoCompraReferencia: '',
    colaboradorCodigo: null,
  };
}

function storageKey(userId: number): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function asOptionalInt(value: unknown): number | '' {
  return typeof value === 'number' && Number.isFinite(value) ? value : '';
}

function asOptionalId(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function asQuery(value: unknown): DataTableQuery {
  const fallback = defaultNfEmpresaGridSnapshot().query;
  if (!value || typeof value !== 'object') return fallback;
  const raw = value as Partial<DataTableQuery>;
  const pageSize =
    typeof raw.pageSize === 'number' && raw.pageSize > 0 ? raw.pageSize : fallback.pageSize;
  const page = typeof raw.page === 'number' && raw.page > 0 ? raw.page : 1;
  const sortOrder = raw.sortOrder === 'asc' || raw.sortOrder === 'desc' ? raw.sortOrder : 'desc';
  return {
    page,
    pageSize,
    search: typeof raw.search === 'string' ? raw.search : undefined,
    sortBy: typeof raw.sortBy === 'string' && raw.sortBy ? raw.sortBy : 'dataHora',
    sortOrder,
  };
}

export function loadNfEmpresaGridState(userId: number | null | undefined): NfEmpresaGridSnapshot | null {
  if (userId == null) return null;
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<NfEmpresaGridSnapshot>;
    const status = STATUS.includes(parsed.status as NotaFiscalEmpresaStatus)
      ? (parsed.status as NotaFiscalEmpresaStatus)
      : '';
    return {
      empresaId: asOptionalId(parsed.empresaId),
      query: asQuery(parsed.query),
      status,
      periodoAno: asOptionalInt(parsed.periodoAno),
      periodoMes: asOptionalInt(parsed.periodoMes),
      emissaoAno: asOptionalInt(parsed.emissaoAno),
      emissaoMes: asOptionalInt(parsed.emissaoMes),
      clienteCodigo: asOptionalId(parsed.clienteCodigo),
      requisicaoCompraReferencia:
        typeof parsed.requisicaoCompraReferencia === 'string'
          ? parsed.requisicaoCompraReferencia
          : '',
      colaboradorCodigo: asOptionalId(parsed.colaboradorCodigo),
    };
  } catch {
    return null;
  }
}

export function saveNfEmpresaGridState(userId: number, snapshot: NfEmpresaGridSnapshot): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(snapshot));
  } catch {
    // Quota ou modo privado: a tela segue sem persistir.
  }
}
